import { useProductReviews } from '../../hooks/useProductReviews';
import { StarRating } from '../ui/StarRating';
import { formatRelativeDate } from '../../utils/relativeDate';

interface ProductReviewsProps {
  productId: number;
}

function ProductReviewsSkeleton() {
  return (
    <div>
      <div className="h-5 w-40 bg-champagne rounded animate-pulse mb-4" />
      <div className="space-y-3">
        {Array.from({ length: 2 }).map((_, idx) => (
          <div key={idx} className="h-20 bg-champagne/50 rounded-xl animate-pulse" />
        ))}
      </div>
    </div>
  );
}

export function ProductReviews({ productId }: ProductReviewsProps) {
  const { reviews, totalCount, averageRating, isLoading, isLoadingMore, error, isEmpty, hasMore, loadMore } =
    useProductReviews(productId);

  if (error) return null;

  return (
    <div className="mt-10 pt-8 border-t border-champagne-dark">
      <h2 className="text-lg font-display font-semibold text-text-dark mb-4">Avaliações</h2>

      {isLoading ? (
        <ProductReviewsSkeleton />
      ) : isEmpty ? (
        <p className="text-sm text-text-light">Ainda não há avaliações para este produto.</p>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-5">
            <StarRating value={averageRating} size="md" />
            <span className="text-sm font-semibold text-text-dark">{averageRating.toFixed(1)}</span>
            <span className="text-sm text-text-light">
              ({totalCount} {totalCount === 1 ? 'avaliação' : 'avaliações'})
            </span>
          </div>

          <ul className="space-y-4 max-w-2xl">
            {reviews.map((review) => (
              <li key={review.id} className="border-b border-champagne last:border-b-0 pb-4 last:pb-0">
                <div className="flex items-center justify-between gap-3 mb-1.5">
                  <p className="text-sm font-semibold text-text-dark">
                    {review.reviewerFirstName}
                    {review.reviewerCity && (
                      <span className="text-text-light font-normal"> · {review.reviewerCity}</span>
                    )}
                  </p>
                  <span className="text-xs text-text-light whitespace-nowrap">
                    {formatRelativeDate(review.createdAt)}
                  </span>
                </div>
                <StarRating value={review.rating} size="sm" />
                {review.comment && (
                  <p className="text-sm text-text-medium mt-2 leading-relaxed">{review.comment}</p>
                )}
              </li>
            ))}
          </ul>

          {hasMore && (
            <button
              type="button"
              onClick={loadMore}
              disabled={isLoadingMore}
              className="mt-4 text-sm font-semibold text-chocolate hover:text-chocolate-dark transition-colors disabled:opacity-60"
            >
              {isLoadingMore ? 'Carregando...' : 'Ver mais avaliações'}
            </button>
          )}
        </>
      )}
    </div>
  );
}
